/**
 * Reports list page.
 * 
 * @module pages/ReportsPage
 * @see CSDEV-143 - Reports page: add business/user filters and date range filter UI
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DataTable, Pagination, Badge, Card } from '../components/ui';
import { useReports, usePagination, useAuditLog, useAllBusinesses, useAllUsers, useExclusionRules } from '../hooks';
import { AuditActionTypes } from '../lib/audit';
import { isExcludedUser } from '../config/excluded-users';
import { formatDate } from '../lib/utils';
import type { FullReportWithRelations, DataTableColumn } from '../types';

/**
 * Reports list page component.
 * 
 * @returns Reports page element
 */
export function ReportsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [statusFilter, setStatusFilter] = useState('');
  const [internalFilter, setInternalFilter] = useState<'all' | 'external' | 'internal'>('external');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sortBy, setSortBy] = useState<string>('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const { page, pageSize, goToPage, changePageSize, pageSizeOptions } = usePagination();
  const { logAction } = useAuditLog();

  // Get filters from URL params
  const businessFilter = searchParams.get('business') || '';
  const userFilter = searchParams.get('user') || '';

  // Fetch filter options
  const { data: businesses } = useAllBusinesses();
  const { data: users } = useAllUsers();
  const { data: exclusionRules, isLoading: exclusionRulesLoading } = useExclusionRules();

  const isFilteringByInternal = internalFilter !== 'all';
  const { data, isLoading, error } = useReports({
    page: isFilteringByInternal ? 1 : page,
    pageSize: isFilteringByInternal ? 10000 : pageSize,
    businessId: businessFilter || undefined,
    userId: userFilter || undefined,
    status: (statusFilter as 'pending' | 'running' | 'completed' | 'failed') || undefined,
    dateFrom: dateFrom ? new Date(dateFrom).toISOString() : undefined,
    dateTo: dateTo ? new Date(dateTo + 'T23:59:59').toISOString() : undefined,
    sortBy,
    sortOrder,
  });

  /**
   * Handles column sort clicks.
   * Toggles order if same column, resets to desc for new column.
   * @see CSDEV-147 - Wire up DataTable sorting
   */
  const handleSort = useCallback((key: string) => {
    if (sortBy === key) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(key);
      setSortOrder('desc');
    }
    goToPage(1);
  }, [sortBy, sortOrder, goToPage]);

  /**
   * Updates URL search params for filters.
   */
  const updateFilter = (key: string, value: string) => {
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
    setSearchParams(params);
    goToPage(1);
  };

  /**
   * Clears all filters.
   */
  const clearFilters = () => {
    setSearchParams({});
    setStatusFilter('');
    setInternalFilter('external');
    setDateFrom('');
    setDateTo('');
    goToPage(1);
  };

  const hasActiveFilters = businessFilter || userFilter || statusFilter || dateFrom || dateTo || internalFilter !== 'external';

  // Log page view
  useEffect(() => {
    logAction(AuditActionTypes.VIEW_REPORTS_LIST);
  }, [logAction]);

  const handleRowClick = useCallback(
    (report: FullReportWithRelations) => {
      navigate(`/reports/${report.id}`);
    },
    [navigate]
  );

  const filteredData = data?.data.filter((report) => {
    if (internalFilter === 'all') return true;
    const excluded = isExcludedUser(report.users?.email, exclusionRules);
    return internalFilter === 'internal' ? excluded : !excluded;
  });

  const columns: DataTableColumn<FullReportWithRelations>[] = [
    {
      key: 'created_at',
      header: 'Date',
      sortable: true,
      render: (report) => (
        <span className="text-gray-500">{formatDate(report.created_at)}</span>
      ),
    },
    {
      key: 'businesses',
      header: 'Business',
      render: (report) => (
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (report.businesses?.id) {
              navigate(`/businesses/${report.businesses.id}`);
            }
          }}
          className="text-primary-600 hover:text-primary-700 font-medium"
        >
          {report.businesses?.business_name || '—'}
        </button>
      ),
    },
    {
      key: 'users',
      header: 'Owner',
      render: (report) => (
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (report.users?.id) {
              navigate(`/users/${report.users.id}`);
            }
          }}
          className="text-gray-600 hover:text-gray-900"
        >
          <span className="flex items-center gap-2">
            {report.users?.email || '—'}
            {isExcludedUser(report.users?.email, exclusionRules) && (
              <Badge variant="neutral" size="sm">Internal</Badge>
            )}
          </span>
        </button>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (report) => (
        <Badge
          variant={
            report.status === 'completed'
              ? 'success'
              : report.status === 'failed'
              ? 'error'
              : report.status === 'running'
              ? 'warning'
              : 'neutral'
          }
          size="sm"
        >
          {report.status}
        </Badge>
      ),
    },
    {
      key: 'avg_ai_visibility_score',
      header: 'AI Score',
      align: 'right',
      render: (report) =>
        report.avg_ai_visibility_score !== null
          ? `${(report.avg_ai_visibility_score * 100).toFixed(1)}%`
          : '—',
    },
    {
      key: 'total_mentions',
      header: 'Mentions',
      align: 'right',
      render: (report) => report.total_mentions ?? '—',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
        <h1 className="text-2xl font-bold text-gray-900">Analysis Reports</h1>
        <p className="text-gray-500">
          View all AI visibility analysis reports across businesses
        </p>
      </div>

      {/* Filters */}
      <Card padding="sm">
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Business filter */}
            <select
              value={businessFilter}
              onChange={(e) => updateFilter('business', e.target.value)}
              className="input w-full sm:w-56"
            >
              <option value="">All businesses</option>
              {businesses?.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.business_name}
                </option>
              ))}
            </select>

            {/* User filter */}
            <select
              value={userFilter}
              onChange={(e) => updateFilter('user', e.target.value)}
              className="input w-full sm:w-56"
            >
              <option value="">All users</option>
              {users?.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.email}
                </option>
              ))}
            </select>

            {/* Status filter */}
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                goToPage(1);
              }}
              className="input w-full sm:w-40"
            >
              <option value="">All statuses</option>
              <option value="pending">Pending</option>
              <option value="running">Running</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
            </select>

            {/* Internal/External filter */}
            <select
              value={internalFilter}
              onChange={(e) => {
                setInternalFilter(e.target.value as 'all' | 'external' | 'internal');
                goToPage(1);
              }}
              disabled={exclusionRulesLoading}
              className="input w-full sm:w-48"
            >
              <option value="all">All reports</option>
              <option value="external">External only</option>
              <option value="internal">Internal only</option>
            </select>
          </div>

          {/* Date range filters */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex items-center gap-2">
              <label htmlFor="dateFrom" className="text-sm text-gray-600 whitespace-nowrap">From:</label>
              <input
                type="date"
                id="dateFrom"
                value={dateFrom}
                onChange={(e) => {
                  setDateFrom(e.target.value);
                  goToPage(1);
                }}
                className="input w-full sm:w-40"
              />
            </div>
            <div className="flex items-center gap-2">
              <label htmlFor="dateTo" className="text-sm text-gray-600 whitespace-nowrap">To:</label>
              <input
                type="date"
                id="dateTo"
                value={dateTo}
                onChange={(e) => {
                  setDateTo(e.target.value);
                  goToPage(1);
                }}
                className="input w-full sm:w-40"
              />
            </div>

            {/* Clear filters button */}
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="btn-secondary text-sm"
              >
                Clear filters
              </button>
            )}
          </div>
        </div>
      </Card>

      {/* Error state */}
      {error && (
        <Card>
          <p className="text-error-600">Failed to load reports: {error.message}</p>
        </Card>
      )}

      {/* Data table */}
      <Card padding="none">
        <DataTable
          columns={columns}
          data={filteredData || []}
          isLoading={isLoading}
          emptyMessage="No reports found"
          onRowClick={handleRowClick}
          keyExtractor={(report) => report.id}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={handleSort}
        />
        {data && isFilteringByInternal ? (
          <div className="px-4 py-3 text-sm text-gray-500 border-t border-gray-200">
            {filteredData?.length ?? 0} {internalFilter} report{(filteredData?.length ?? 0) !== 1 ? 's' : ''} found
          </div>
        ) : data && data.totalPages > 0 ? (
          <Pagination
            currentPage={page}
            totalPages={data.totalPages}
            onPageChange={goToPage}
            pageSize={pageSize}
            onPageSizeChange={changePageSize}
            totalItems={data.count}
            pageSizeOptions={pageSizeOptions}
          />
        ) : null}
      </Card>
    </div>
  );
}
